import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH13_QUESTIONS } from "../data/neetPhysicsCh13Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 13: Kinetic Theory of Gases (NCERT Class 11, Ch. 13).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Ideal Gas Laws", "Kinetic Theory & Pressure", "Degrees of Freedom & Specific Heats", "Molecular Speeds & Mean Free Path"];

const SECTION_COLORS = {
  "Ideal Gas Laws":                          "#0891b2",
  "Kinetic Theory & Pressure":                "#d97706",
  "Degrees of Freedom & Specific Heats":      "#9333ea",
  "Molecular Speeds & Mean Free Path":        "#dc2626",
};

const CONCEPTS = [
  // ── Ideal Gas Laws ──────────────────────────────────────────────────────────
  { section: "Ideal Gas Laws", title: "Postulates of the kinetic theory of gases",
    explanation: "The kinetic theory of gases makes several idealized assumptions about an ideal gas: molecules are point masses (negligible size compared to the container and their separation); molecules are in continuous, random motion, colliding with each other and the container walls; collisions are perfectly elastic (kinetic energy and momentum are conserved); molecules exert no force on one another except during a collision (negligible intermolecular forces otherwise); the time spent in a collision is negligible compared to the time between collisions; and the molecules obey Newton's laws of motion, travelling in straight lines between collisions. These assumptions let the macroscopic gas laws (Boyle's, Charles's) be derived from molecular motion.",
    example: `Ideal gas molecule assumptions:
Size of molecule   << average separation   (point-like)
Collisions          -> perfectly elastic (KE, momentum conserved)
Force between molecules = 0   (except during collision)
Motion              -> random, straight lines between collisions` },
  { section: "Ideal Gas Laws", title: "Ideal gas equation — PV = nRT and PV = NkT",
    explanation: "For n moles of an ideal gas, PV = nRT, where R = 8.314 J/(mol.K) is the universal gas constant. Equivalently, in terms of the number of molecules N, PV = NkT, where k = R/N_A = 1.38 x 10^-23 J/K is Boltzmann's constant and N_A = 6.022 x 10^23 /mol is Avogadro's number. Both forms describe the same ideal gas behaviour -- R applies per mole, k applies per molecule.",
    example: `R = 8.314 J/(mol.K),  k = R/N_A = 1.38 x 10^-23 J/K

1 mole of ideal gas at STP (T = 273 K, P = 1.013 x 10^5 Pa):
V = nRT/P = (1)(8.314)(273) / (1.013 x 10^5)
  = 2270 / 1.013 x 10^5 ~ 0.0224 m^3 = 22.4 L   (standard molar volume)` },
  { section: "Ideal Gas Laws", title: "Boyle's law and Charles's law as special cases",
    explanation: "Boyle's law (constant T, n): PV = constant, so P1V1 = P2V2 -- pressure and volume are inversely proportional. Charles's law (constant P, n): V/T = constant, so V1/T1 = V2/T2 -- volume is directly proportional to absolute temperature. Both follow directly from PV = nRT by holding one variable fixed.",
    example: `Boyle's law (isothermal): P1 V1 = P2 V2
  2 L at 1 atm compressed to 1 L  ->  P2 = P1V1/V2 = (1)(2)/1 = 2 atm

Charles's law (isobaric): V1/T1 = V2/T2
  2 L at 300 K heated to 600 K at constant P
  ->  V2 = V1(T2/T1) = 2 x (600/300) = 4 L` },
  { section: "Ideal Gas Laws", title: "Dalton's law of partial pressures",
    explanation: "For a mixture of non-reacting ideal gases occupying the same container, the total pressure equals the sum of the partial pressures each gas would exert if it alone occupied that volume at the same temperature: P = P1 + P2 + P3 + .... This follows from kinetic theory because each gas's molecules independently contribute to the total momentum transferred to the walls.",
    example: `P_total = P1 + P2 + P3 + ...

Container with N2 (partial pressure 0.6 atm) and O2 (partial
pressure 0.4 atm) at the same T, V:
P_total = 0.6 + 0.4 = 1.0 atm` },

  // ── Kinetic Theory & Pressure ───────────────────────────────────────────────
  { section: "Kinetic Theory & Pressure", title: "Pressure of an ideal gas from kinetic theory",
    explanation: "Kinetic theory shows (derivation not required at this level) that the pressure exerted by an ideal gas is P = (1/3)rho<v^2>, where rho is the gas density and <v^2> is the mean of the squared molecular speeds. Equivalently, P = (1/3)(M/V)v_rms^2, where M is the total mass of gas in volume V and v_rms = sqrt(<v^2>) is the root-mean-square speed. This shows pressure arises from the constant bombardment of molecules on the container walls.",
    example: `P = (1/3) . rho . <v^2>          (rho = density, <v^2> = mean square speed)

O2 gas at T = 300 K: rho ~ 1.3 kg/m^3, v_rms ~ 483.6 m/s
P = (1/3)(1.3)(483.6)^2 = (1/3)(1.3)(233,871)
  = (1/3)(304,032) ~ 1.013 x 10^5 Pa   (matches atmospheric pressure)` },
  { section: "Kinetic Theory & Pressure", title: "Kinetic interpretation of temperature",
    explanation: "Combining P = (1/3)(N/V)m<v^2> with the ideal gas equation PV = NkT gives (1/2)m<v^2> = (3/2)kT -- the average translational kinetic energy of a single gas molecule depends ONLY on the absolute temperature, not on the gas's mass or identity. This is the kinetic interpretation of temperature: temperature is a direct measure of the average molecular kinetic energy.",
    example: `Average KE per molecule = (3/2) k T   (independent of molecular mass!)

At T = 300 K, k = 1.38 x 10^-23 J/K:
Average KE = 1.5 x (1.38 x 10^-23) x 300 = 6.21 x 10^-21 J
(same value for He, O2, or any ideal gas molecule at 300 K)` },
  { section: "Kinetic Theory & Pressure", title: "Relating pressure, kinetic energy and temperature (PV = (2/3)E)",
    explanation: "The total translational kinetic energy of n moles of an ideal gas is E = (3/2)nRT. Combining this with PV = nRT gives the useful relation PV = (2/3)E -- the product of pressure and volume is two-thirds of the gas's total translational kinetic energy.",
    example: `E (total translational KE) = (3/2) n R T
PV = n R T = (2/3) E

For 1 mole at T = 300 K:
E = 1.5 x 8.314 x 300 = 3741.3 J
PV = 8.314 x 300 = 2494.2 J
Check: (2/3) x 3741.3 = 2494.2 J = PV   (matches)` },
  { section: "Kinetic Theory & Pressure", title: "Avogadro's law as a consequence of kinetic theory",
    explanation: "Avogadro's law states that equal volumes of any ideal gas, at the same temperature and pressure, contain equal numbers of molecules. This follows directly from PV = NkT: at fixed P, V and T, N = PV/(kT) is the same number regardless of which gas it is -- the law does not depend on the mass or chemical identity of the molecules.",
    example: `N = PV / (kT)   -- same P, V, T  =>  same N for ANY gas

At STP, 22.4 L of ANY ideal gas contains N_A = 6.022 x 10^23
molecules -- true for He, O2, CO2, etc. alike` },

  // ── Degrees of Freedom & Specific Heats ─────────────────────────────────────
  { section: "Degrees of Freedom & Specific Heats", title: "Degrees of freedom of gas molecules",
    explanation: "The number of degrees of freedom (f) of a molecule is the number of independent ways it can store energy (independent coordinates needed to specify its position and orientation). A monatomic molecule (e.g. He, Ar) has only translational motion along 3 axes: f = 3. A diatomic molecule (e.g. O2, N2, H2) also has 2 rotational degrees of freedom (rotation about the two axes perpendicular to the bond) at moderate temperatures: f = 3 + 2 = 5. At very high temperatures, vibrational motion adds 2 more (1 kinetic + 1 potential): f = 7. A non-linear triatomic molecule (e.g. H2O) has 3 translational + 3 rotational: f = 6.",
    example: `Monatomic (He, Ar):         f = 3   (translational only)
Diatomic (O2, N2), usual T:  f = 5   (3 trans + 2 rotational)
Diatomic, very high T:       f = 7   (+2 vibrational)
Non-linear triatomic (H2O):  f = 6   (3 trans + 3 rotational)` },
  { section: "Degrees of Freedom & Specific Heats", title: "Law of equipartition of energy",
    explanation: "The law of equipartition of energy states that in thermal equilibrium, the total energy of a system is shared EQUALLY among all its degrees of freedom, with each degree of freedom contributing an average energy of (1/2)kT per molecule (or (1/2)RT per mole). So a molecule with f degrees of freedom has average total energy (f/2)kT.",
    example: `Each degree of freedom contributes (1/2) k T per molecule

Monatomic gas (f=3):  avg energy = (3/2) k T
Diatomic gas (f=5):   avg energy = (5/2) k T
  -- 3 translational x (1/2)kT + 2 rotational x (1/2)kT = (5/2)kT` },
  { section: "Degrees of Freedom & Specific Heats", title: "Specific heats of a monatomic gas",
    explanation: "For a monatomic ideal gas (f = 3), the internal energy of n moles is U = (3/2)nRT. Since Cv = (1/n)(dU/dT), Cv = (3/2)R. Using Mayer's relation Cp = Cv + R, Cp = (5/2)R. The ratio of specific heats gamma = Cp/Cv = 5/3 ~ 1.67.",
    example: `Monatomic gas (f = 3):
Cv = (3/2) R = 1.5 x 8.314 = 12.47 J/(mol.K)
Cp = (5/2) R = 2.5 x 8.314 = 20.79 J/(mol.K)
gamma = Cp/Cv = 5/3 ~ 1.67` },
  { section: "Degrees of Freedom & Specific Heats", title: "Specific heats of diatomic/polyatomic gases and Mayer's relation",
    explanation: "In general, for a gas with f degrees of freedom, U = (f/2)nRT, so Cv = (f/2)R and Cp = Cv + R = ((f+2)/2)R (Mayer's relation: Cp - Cv = R, per mole). For a rigid diatomic gas (f=5): Cv = (5/2)R, Cp = (7/2)R, gamma = 7/5 = 1.4. For a non-linear triatomic gas (f=6): Cv = 3R, Cp = 4R, gamma = 4/3 ~ 1.33.",
    example: `General: Cv = (f/2)R,  Cp = Cv + R,  gamma = Cp/Cv = (f+2)/f

Diatomic (f=5): Cv = 2.5(8.314) = 20.79, Cp = 29.10 J/(mol.K)
  Check Mayer: Cp - Cv = 29.10 - 20.79 = 8.31 ~ R   (matches)
  gamma = 7/5 = 1.4

Triatomic non-linear (f=6): Cv = 3R = 24.94, Cp = 4R = 33.26
  gamma = 4/3 ~ 1.33` },

  // ── Molecular Speeds & Mean Free Path ────────────────────────────────────────
  { section: "Molecular Speeds & Mean Free Path", title: "RMS speed of gas molecules",
    explanation: "The root-mean-square (rms) speed of gas molecules is v_rms = sqrt(<v^2>) = sqrt(3RT/M) = sqrt(3kT/m), where M is the molar mass (kg/mol) and m is the mass of one molecule. It increases with temperature and decreases with molar mass -- lighter molecules move faster on average at the same temperature.",
    example: `v_rms = sqrt(3RT/M)

O2 (M = 0.032 kg/mol) at T = 300 K:
v_rms = sqrt(3 x 8.314 x 300 / 0.032) = sqrt(233,831) ~ 483.6 m/s

H2 (M = 0.002 kg/mol) at T = 300 K:
v_rms = sqrt(3 x 8.314 x 300 / 0.002) = sqrt(3,741,300) ~ 1934 m/s
(H2 is 16x lighter than O2, so v_rms is 4x larger: sqrt(16) = 4)` },
  { section: "Molecular Speeds & Mean Free Path", title: "Average speed and most probable speed",
    explanation: "Besides v_rms, two other characteristic speeds describe the Maxwell speed distribution: the average speed v_avg = sqrt(8RT/(pi.M)), and the most probable speed v_p (the speed at the distribution's peak) = sqrt(2RT/M). For any ideal gas at a given temperature, v_rms > v_avg > v_p, in the fixed ratio sqrt(3) : sqrt(8/pi) : sqrt(2) = 1.732 : 1.596 : 1.414.",
    example: `v_rms = sqrt(3RT/M),  v_avg = sqrt(8RT/(pi.M)),  v_p = sqrt(2RT/M)

O2 at T = 300 K (M = 0.032 kg/mol):
v_rms ~ 483.6 m/s,  v_avg ~ 445.5 m/s,  v_p ~ 394.8 m/s
Ratio check: 483.6 : 445.5 : 394.8 ~ 1.732 : 1.596 : 1.414   (matches)
So v_rms > v_avg > v_p always` },
  { section: "Molecular Speeds & Mean Free Path", title: "Mean free path — definition and formula",
    explanation: "The mean free path (lambda) is the average distance a gas molecule travels between successive collisions with other molecules. It is given by lambda = 1/(sqrt(2).pi.d^2.n), where d is the molecular diameter and n is the number density of molecules (N/V). The factor sqrt(2) accounts for the relative motion of the colliding molecules (both are moving, not just the target).",
    example: `lambda = 1 / (sqrt(2) . pi . d^2 . n)

Air-like gas at STP: d ~ 3 x 10^-10 m, n ~ 2.7 x 10^25 /m^3
pi.d^2 = 3.1416 x 9x10^-20 = 2.827 x 10^-19 m^2
sqrt(2) x that = 3.998 x 10^-19 m^2
lambda = 1 / (3.998x10^-19 x 2.7x10^25) ~ 9.3 x 10^-8 m  (~93 nm)` },
  { section: "Molecular Speeds & Mean Free Path", title: "Factors affecting the mean free path",
    explanation: "Since n = N/V = P/(kT) for an ideal gas, the mean free path can also be written lambda = kT/(sqrt(2).pi.d^2.P). This shows lambda is DIRECTLY proportional to T (at constant P) and INVERSELY proportional to P (at constant T) -- a gas expanded to lower pressure has molecules travelling farther between collisions. Lambda is also inversely proportional to d^2, so larger molecules collide more often and have a shorter mean free path.",
    example: `lambda = kT / (sqrt(2) . pi . d^2 . P)   proportional to T/P

If pressure is doubled at constant T:  lambda is halved
If temperature is doubled at constant P:  lambda is doubled
Larger molecular diameter d  ->  smaller lambda (more frequent collisions)` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh13Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh13Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh13Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH13_QUESTIONS : NEET_PHYSICS_CH13_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 13</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Kinetic Theory of Gases</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Ch. 13 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH13_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH13_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
